import {
  type PropsWithChildren,
  type MouseEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import { parseGid } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import { preload } from "swr";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useProduct, useProductParams } from "@react/hooks/useProduct";
import { useRelatedGiftWithPurchaseItems } from "@react/hooks/useRelatedGiftWithPurchaseItems";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  detailsByProductAndSelectedOptions,
  selectedOrFirstAvailableVariant,
} from "@react/utilities";
import { getMsrpForVariant } from "@root/tablescape/utilities";
import {
  STOREFRONT_CART_ATTRIBUTES,
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type CartGiftWithPurchaseAttributeValue,
  type CartGiftWithPurchaseDetails,
  type CartProduct,
  type CartProductVariant,
  type TablescapeState,
  type CartGiftWithPurchaseContext,
  type CartSpendingBasedGiftWithPurchase,
  type CartSectionSettings,
} from "@tablescape/state";

const CartGiftWithPurchaseContext = createContext<
  CartGiftWithPurchaseContext | undefined
>(undefined);

type Props = {
  value?: CartGiftWithPurchaseContext;
};

function CartGiftWithPurchaseProvider({
  children,
  value: providedValue,
}: PropsWithChildren<Props>) {
  const {
    text: { giftLabel },
  } = useSectionSettings<CartSectionSettings>();

  const [activeCartLineId, setActiveCartLineId] = useState<
    CartLine["id"] | undefined
  >();
  const [eligibleActiveVariant, setEligibleActiveVariant] = useState<
    CartProductVariant | undefined
  >();
  const [
    eligibleCartSpendingGiftWithPurchase,
    setEligibleCartSpendingGiftWithPurchase,
  ] = useState<CartSpendingBasedGiftWithPurchase | undefined>(undefined);

  const [confirmedProductDetails, setConfirmedProductDetails] = useState<
    | {
        productId: CartProduct["id"];
        selectedOptions?: Partial<SelectedOption>[];
      }
    | undefined
  >();
  const [selectedGiftProductId, setSelectedGiftProductId] =
    useState<CartProduct["id"]>("");
  const [selectedGiftVariant, setSelectedGiftVariant] =
    useState<CartProductVariant>();

  const [isGiftWithPurchaseLoading, setIsGiftWithPurchaseLoading] =
    useState(false);

  const { clearProductDetails, toggleCartDrawer } = useCartActiveProduct();

  const {
    ui: { cartLineAdding, cartLineRemoving, cartLineUpdating },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );
  const { relatedCartLines, relatedCartLinesUpdating } =
    useRelatedGiftWithPurchaseItems();

  const cartLineChanging = useMemo(
    () => cartLineAdding || cartLineRemoving || cartLineUpdating,
    [cartLineAdding, cartLineRemoving, cartLineUpdating]
  );

  const handleCloseGiftWithPurchaseDrawer = useCallback(() => {
    toggleCartDrawer({ giftWithPurchaseDrawerOpen: false });
  }, [toggleCartDrawer]);

  const giftWithPurchaseDetails = useMemo<
    CartGiftWithPurchaseDetails[] | undefined
  >(() => {
    if (eligibleActiveVariant) {
      return eligibleActiveVariant.giftWithPurchase?.products?.map((product) =>
        detailsByProductAndSelectedOptions(
          product,
          eligibleActiveVariant.selectedOptions
        )
      );
    }
    return eligibleCartSpendingGiftWithPurchase?.products?.map((product) =>
      detailsByProductAndSelectedOptions(product)
    );
  }, [eligibleActiveVariant, eligibleCartSpendingGiftWithPurchase?.products]);

  useEffect(() => {
    if (!giftWithPurchaseDetails) {
      return;
    } else if (
      giftWithPurchaseDetails &&
      (!selectedGiftProductId || selectedGiftProductId === "")
    ) {
      setSelectedGiftProductId(giftWithPurchaseDetails[0]?.product.id ?? "");
      return;
    }

    setSelectedGiftVariant(
      giftWithPurchaseDetails?.find(
        ({ product }) => product.id === selectedGiftProductId
      )?.selectedVariant
    );
  }, [giftWithPurchaseDetails, selectedGiftProductId]);

  const handleConfirmSelectedGiftWithPurchase = useCallback(() => {
    const gwpAttributeValue = {
      [CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.CART_LINE_ID]:
        eligibleCartSpendingGiftWithPurchase ? undefined : activeCartLineId,
      [CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.GIFT_BADGE]:
        eligibleCartSpendingGiftWithPurchase?.giftItemBadgeText || giftLabel,
      [CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD]:
        eligibleCartSpendingGiftWithPurchase?.spendingThreshold,
      [CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.CREATED_AT]: Date.now(),
    } satisfies CartGiftWithPurchaseAttributeValue;

    if (!selectedGiftVariant || !selectedGiftVariant.id) {
      handleCloseGiftWithPurchaseDrawer();
      return;
    }

    setIsGiftWithPurchaseLoading(true);

    TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
      cart: {
        lines: [
          {
            attributes: [
              {
                key: STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
                value: JSON.stringify(gwpAttributeValue),
              },
              {
                key: STOREFRONT_CART_ATTRIBUTES.MSRP,
                value: getMsrpForVariant(selectedGiftVariant),
              },
            ],
            id: parseGid(selectedGiftVariant.id).id,
            quantity: 1,
          },
        ],
      },
    });
  }, [
    activeCartLineId,
    eligibleCartSpendingGiftWithPurchase,
    giftLabel,
    handleCloseGiftWithPurchaseDrawer,
    selectedGiftVariant,
  ]);

  const handleBeforeEnter = useCallback(() => {}, []);

  const handleAfterLeave = useCallback(() => {
    requestAnimationFrame(() => {
      setEligibleCartSpendingGiftWithPurchase(undefined);
      clearProductDetails();
      setSelectedGiftProductId("");
      setSelectedGiftVariant(undefined);
    });
  }, [
    clearProductDetails,
    setSelectedGiftProductId,
    setSelectedGiftVariant,
    setEligibleCartSpendingGiftWithPurchase,
  ]);

  const { product } = useProduct(confirmedProductDetails?.productId);

  const handlePreloadProduct = useCallback(
    (productId: CartProduct["id"]) => (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      preload([...useProductParams, { productId }], storefrontAPIFetcher);
    },
    []
  );

  const handleOpenProductVariantDrawer = useCallback(
    (
        productId: CartProduct["id"],
        selectedOptions?: Partial<SelectedOption>[]
      ) =>
      () => {
        setConfirmedProductDetails({
          productId,
          selectedOptions,
        });
      },
    []
  );

  // Effect that closes the drawer when the cart line is updated
  useEffect(() => {
    if (
      isGiftWithPurchaseLoading &&
      !cartLineChanging &&
      !relatedCartLinesUpdating
    ) {
      setIsGiftWithPurchaseLoading(false);
      handleCloseGiftWithPurchaseDrawer();
    }
  }, [
    cartLineChanging,
    handleCloseGiftWithPurchaseDrawer,
    isGiftWithPurchaseLoading,
    relatedCartLinesUpdating,
  ]);

  // Effect that forwards the user to the product variant drawer when the product is selected
  useEffect(() => {
    if (
      !product ||
      !confirmedProductDetails ||
      product?.id !== confirmedProductDetails?.productId
    ) {
      return;
    }

    toggleCartDrawer(
      {
        productVariantDrawerOpen: true,
      },
      {
        activeOptions: confirmedProductDetails.selectedOptions,
        activeProduct: product,
        activeUpsell: undefined,
        activeVariant: selectedOrFirstAvailableVariant(
          product,
          confirmedProductDetails.selectedOptions
        ),
        isGiftWithPurchase: true,
        isProductUpdate: false,
      }
    );

    setConfirmedProductDetails(undefined);
  }, [confirmedProductDetails, product, toggleCartDrawer]);

  const value = useMemo<CartGiftWithPurchaseContext>(
    () => ({
      ...providedValue,
      eligibleCartSpendingGiftWithPurchase,
      giftWithPurchaseDetails,
      handleAfterLeave,
      handleBeforeEnter,
      handleCloseGiftWithPurchaseDrawer,
      handleConfirmSelectedGiftWithPurchase,
      handleOpenProductVariantDrawer,
      handlePreloadProduct,
      isGiftWithPurchaseLoading,
      relatedCartLines,
      relatedCartLinesUpdating,
      selectedGiftProductId,
      selectedGiftVariant,
      setActiveCartLineId,
      setEligibleActiveVariant,
      setEligibleCartSpendingGiftWithPurchase,
      setSelectedGiftProductId,
      setSelectedGiftVariant,
    }),
    [
      eligibleCartSpendingGiftWithPurchase,
      giftWithPurchaseDetails,
      handleAfterLeave,
      handleBeforeEnter,
      handleCloseGiftWithPurchaseDrawer,
      handleConfirmSelectedGiftWithPurchase,
      handleOpenProductVariantDrawer,
      handlePreloadProduct,
      isGiftWithPurchaseLoading,
      providedValue,
      relatedCartLines,
      relatedCartLinesUpdating,
      selectedGiftProductId,
      selectedGiftVariant,
    ]
  );

  return (
    <CartGiftWithPurchaseContext.Provider value={value}>
      {children}
    </CartGiftWithPurchaseContext.Provider>
  );
}

function useCartGiftWithPurchase() {
  const context = useContext<CartGiftWithPurchaseContext | undefined>(
    CartGiftWithPurchaseContext
  );

  if (context === undefined) {
    throw new Error(
      "useCartGiftWithPurchase must be used within a CartGiftWithPurchaseProvider"
    );
  }

  return context;
}

export { CartGiftWithPurchaseProvider, useCartGiftWithPurchase };
